"use client";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faWindows } from "@fortawesome/free-brands-svg-icons";
import {
	faArrowUpRightFromSquare,
	faCheck,
	faCommand,
	faLink,
	faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadIcon, cn } from "@tosize/ui";
import { Command } from "cmdk";
import { match } from "ts-pattern";
import { type CMDKPage, useCommand } from "~/app/[lang]/command/Context";
import { useRouter } from "~/hooks/useRouter";

interface CommandItemProps {
	children: React.ReactNode;
	value?: string;
	icon?: IconProp;
	shortcut?: string;
	metaKey?: boolean;
	shiftKey?: boolean;
	active?: boolean;
	disabled?: boolean;
	isLoading?: boolean;
	className?: string;
}

type VariantProps =
	| { variant?: "backlink"; id: string; onSelect?: never }
	| { variant?: "link"; id: string; onSelect?: never }
	| { variant?: "page"; id: CMDKPage; onSelect?: never }
	| { variant?: "default"; id?: never; onSelect?: () => void };

export function CommandItem(props: CommandItemProps & VariantProps) {
	const {
		id,
		onSelect,
		children,
		icon,
		shortcut,
		metaKey = false,
		shiftKey = true,
		variant,
		active,
		disabled,
		isLoading,
		className,
		...remaining
	} = props;

	const router = useRouter();
	const { setPages } = useCommand();

	const platform = navigator?.userAgentData?.platform || navigator?.platform || "unknown";
	const isMac = platform.indexOf("mac") === 0 || platform.indexOf("Mac") === 0;

	return (
		<Command.Item
			className={cn(
				"mb-1 flex items-center rounded px-2 py-1 text-base text-dark last:mb-0 aria-selected:bg-shades-light motion-safe:transition",
				{
					"cursor-pointer": !(disabled || isLoading),
					"cursor-not-allowed bg-shades-light/70 opacity-70": disabled || isLoading,
				},
				className,
			)}
			disabled={disabled || isLoading}
			onSelect={() => {
				if (variant === "backlink") {
					return router.push(id);
				}

				if (variant === "link") {
					return window.open(id, "_blank");
				}

				if (variant === "page") {
					return setPages((pages) => [...pages, id]);
				}

				onSelect?.();
			}}
			{...remaining}
		>
			{icon && <FontAwesomeIcon icon={icon} fixedWidth className="mr-2 text-shades-dark text-sm" />}

			{children}

			<div className="ml-auto">
				{isLoading ? (
					<LoadIcon />
				) : active !== undefined && !disabled ? (
					<FontAwesomeIcon
						icon={active ? faCheck : faTimes}
						fixedWidth
						className={cn({
							"text-success": active,
							"text-danger": !active,
						})}
					/>
				) : shortcut ? (
					<div className="flex space-x-1">
						{metaKey && (
							<kbd className="flex min-w-[20px] items-center justify-center rounded border border-shades bg-shades-medium-light px-1 py-0.5 font-sans text-xs">
								<FontAwesomeIcon icon={isMac ? faCommand : faWindows} />
							</kbd>
						)}

						{shiftKey && (
							<kbd className="flex min-w-[20px] items-center justify-center rounded border border-shades bg-shades-medium-light px-1 py-0.5 font-sans text-xs">
								⇧
							</kbd>
						)}

						{shortcut.split(" ").map((key) => {
							return (
								<kbd
									className="flex min-w-[20px] items-center justify-center rounded border border-shades bg-shades-medium-light px-1 py-0.5 font-sans text-xs"
									key={key}
								>
									{key.toUpperCase()}
								</kbd>
							);
						})}
					</div>
				) : (
					(variant === "link" || variant === "backlink") && (
						<FontAwesomeIcon
							icon={match(variant)
								.with("link", () => faArrowUpRightFromSquare)
								.with("backlink", () => faLink)
								.run()}
							fixedWidth
							className="text-shades-medium text-sm"
						/>
					)
				)}
			</div>
		</Command.Item>
	);
}
